import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { 
  ScrollText, 
  Loader2,
  Copy,
  BookMarked,
  Plus,
  X,
  Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const suggestedThemes = [
  'El Plan de Salvación',
  'La Expiación de Jesucristo',
  'El Sacerdocio',
  'La Fe',
  'El Arrepentimiento',
  'El Bautismo',
  'El Don del Espíritu Santo',
  'La Restauración',
  'El Libro de Mormón',
  'La Familia Eterna',
  'Los Convenios',
  'La Segunda Venida'
];

const DoctrinalMode = () => {
  const { token } = useAuth();

  const [theme, setTheme] = useState('');
  const [passages, setPassages] = useState([]);
  const [newPassage, setNewPassage] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const addPassage = () => {
    if (newPassage.trim() && !passages.includes(newPassage.trim())) {
      setPassages([...passages, newPassage.trim()]);
      setNewPassage('');
    }
  };

  const removePassage = (passage) => {
    setPassages(passages.filter(p => p !== passage));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!theme.trim()) {
      toast.error('Por favor ingresa un tema doctrinal');
      return;
    }

    if (passages.length === 0) {
      toast.error('Por favor agrega al menos un pasaje de referencia');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ai/doctrinal-depth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          theme: theme,
          passages: passages
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.response);
        toast.success('Análisis generado exitosamente');
      } else {
        toast.error('Error al generar el análisis');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      toast.success('Copiado al portapapeles');
    }
  };

  const saveAsNote = async () => {
    if (!noteTitle.trim()) {
      toast.error('Ingresa un título para la nota');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: noteTitle,
          content: result,
          scripture_ref: passages.join(', '),
          tags: ['doctrinal', theme.toLowerCase()]
        })
      });

      if (response.ok) {
        toast.success('Análisis guardado exitosamente');
        setSaveDialogOpen(false);
        setNoteTitle('');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="doctrinal-mode">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <ScrollText className="h-8 w-8 text-primary" />
            Modo Profundidad Doctrinal
          </h1>
          <p className="text-muted-foreground">
            Explora temas doctrinales con análisis comparativo y referencias oficiales
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Configurar Análisis</CardTitle>
              <CardDescription>
                Selecciona un tema y los pasajes de referencia
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label>Tema Doctrinal *</Label>
                  <Input
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    placeholder="Ej: La Expiación de Jesucristo"
                    data-testid="theme-input"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {suggestedThemes.slice(0, 6).map((t) => (
                      <Badge
                        key={t}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary/10 transition-colors"
                        onClick={() => setTheme(t)}
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Pasajes de Referencia *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newPassage}
                      onChange={(e) => setNewPassage(e.target.value)}
                      placeholder="Ej: Juan 3:16"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPassage())}
                      data-testid="passage-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addPassage}
                      data-testid="add-passage-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {passages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {passages.map((passage) => (
                        <Badge
                          key={passage}
                          variant="secondary"
                          className="gap-1 pr-1"
                        >
                          {passage}
                          <button
                            type="button"
                            onClick={() => removePassage(passage)}
                            className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Agrega múltiples referencias como: Alma 7:11-12, 2 Nefi 2:25, D&C 19:16-19
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="analyze-doctrinal-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generando análisis...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generar Análisis Doctrinal
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Análisis Doctrinal</CardTitle>
              <CardDescription>
                El análisis profundo aparecerá aquí
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar
                    </Button>
                    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BookMarked className="h-4 w-4 mr-1" />
                          Guardar
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Guardar Análisis</DialogTitle>
                          <DialogDescription>
                            Guarda este análisis en tu repositorio personal
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Título</Label>
                            <Input
                              value={noteTitle}
                              onChange={(e) => setNoteTitle(e.target.value)}
                              placeholder={`Análisis: ${theme}`}
                              data-testid="doctrinal-save-title"
                            />
                          </div>
                          <Button
                            onClick={saveAsNote}
                            disabled={saving}
                            className="w-full"
                            data-testid="doctrinal-confirm-save"
                          >
                            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Guardar Análisis
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <ScrollArea className="h-[450px] pr-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none markdown-content">
                      {result.split('\n').map((paragraph, i) => (
                        <p key={`doctrinal-p-${i}`}>{paragraph}</p>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[450px] text-muted-foreground">
                  <ScrollText className="h-16 w-16 mb-4 opacity-20" />
                  <p className="text-center">
                    Selecciona un tema y pasajes para comenzar el análisis
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default DoctrinalMode;
